import { SEARCH_LIMIT } from './file-list.utils.js';

// Unicode-aware word boundaries:
// Start: (?<![\p{L}\p{N}]) - not preceded by a letter or digit
// End: (?![\p{L}\p{N}]) - not followed by a letter or digit
const UNI_B_START = '(?<![\\p{L}\\p{N}])';
const UNI_B_END = '(?![\\p{L}\\p{N}])';

/**
 * Split conditions into exact phrase (boost >= 5) and regular (boost < 5) conditions
 * @param {Array} matchConditions - Original match conditions
 * @returns {Object} { exactPhraseConditions, regularConditions }
 */
function splitConditionsByBoost(matchConditions) {
    const exactPhraseConditions = [];
    const regularConditions = [];
    let wasOrWrapper = false;
    
    for (const level1 of matchConditions) {
        // Check if this level1 contains exact phrase patterns (boost >= 5)
        let hasExactPhrase = false;
        
        if (level1.condition === "or" && level1.value && level1.value.length > 0) {
            // Check OR wrapper for exact phrase patterns
            const isOrWrapper = level1.value[0] && level1.value[0].condition && 
                (level1.value[0].condition === "and" || level1.value[0].condition === "or");
            
            if (isOrWrapper) {
                wasOrWrapper = true;
                // Check nested conditions for exact phrase patterns
                for (const nestedCond of level1.value) {
                    for (const subcond of (nestedCond.value || [])) {
                        for (const patternItem of (subcond.value || [])) {
                            const boost = (typeof patternItem === 'object' && patternItem.boost) ? patternItem.boost : 1;
                            if (boost >= 5) {
                                hasExactPhrase = true;
                                break;
                            }
                        }
                        if (hasExactPhrase) break;
                    }
                    if (hasExactPhrase) break;
                }
            } else {
                // Direct patterns in OR condition
                for (const patternItem of level1.value) {
                    const boost = (typeof patternItem === 'object' && patternItem.boost) ? patternItem.boost : 1;
                    if (boost >= 5) {
                        hasExactPhrase = true;
                        break;
                    }
                }
            }
        } else if (level1.condition === "and" && level1.value && level1.value.length > 0) {
            // Check AND conditions for exact phrase patterns
            for (const level2 of level1.value) {
                if (level2.value && Array.isArray(level2.value)) {
                    for (const patternItem of level2.value) {
                        const boost = (typeof patternItem === 'object' && patternItem.boost) ? patternItem.boost : 1;
                        if (boost >= 5) {
                            hasExactPhrase = true;
                            break;
                        }
                    }
                    if (hasExactPhrase) break;
                }
            }
        }
        
        if (hasExactPhrase) {
            exactPhraseConditions.push(level1);
        } else {
            regularConditions.push(level1);
        }
    }
    
    return { exactPhraseConditions, regularConditions, wasOrWrapper };
}

function searchFilesSinglePass(filesList, matchConditions, { fullPath = false, colorize = false, test = false, limit = null } = {}) {
	let matches = [];
	let strs = colorize ? [filesList] : filesList;
	let processedCount = 0, matchedCount = 0;
	
	for (let i = 0; i < strs.length; i++) {
		processedCount++;
		let str = strs[i];
		let matchConditionTotal = 0;
		let fileMatchDetails = [];
		let fileScore = 0;  // Accumulated score for sorting
		const scoreLog = []; // Debug: track score accumulation
		
		for (const level1Idx in matchConditions) {
			const level1 = matchConditions[level1Idx];
			let matchCondition1 = 0;
			let level1Details = {
				condition: level1.condition,
				matched: false,
				subConditions: []
			};
			
			// Handle OR wrapper (auto-exact phrase + regular terms)
			const isOrWrapper = level1.condition === "or" && 
				level1.value && level1.value.length > 0 &&
				level1.value[0] && level1.value[0].condition && 
				(level1.value[0].condition === "and" || level1.value[0].condition === "or");
			
			if (isOrWrapper) {
				// Recursively check each nested condition - if ANY matches, the OR passes
				let anyNestedMatched = false;
				let bestNestedScore = 0;
				
				for (const nestedCond of level1.value) {
					let nestedCondMatch = 0;
					let nestedScore = 0;
					
					for (const subcond of (nestedCond.value || [])) {
						let subcondMatch = 0;
						let maxBoost = 0;
						
						for (const patternItem of (subcond.value || [])) {
							const pattern = (typeof patternItem === 'object' && patternItem.pattern) ? patternItem.pattern : patternItem;
							const boost = (typeof patternItem === 'object' && patternItem.boost) ? patternItem.boost : 1;
							const searchInDateField = (typeof patternItem === 'object' && patternItem.searchInDateField === true);
							
							try {
								// Explicitly check for undefined/null, not falsy (empty string '' is valid for case-sensitive)
								const flags = (typeof patternItem === 'object' && patternItem.flags !== undefined && patternItem.flags !== null) 
									? patternItem.flags 
									: (subcond.modifier || "i");
								// CRITICAL: Don't use global flag 'g' with test() - it causes state issues
								// CRITICAL: Add 'u' flag for Unicode support (needed for Cyrillic word boundaries)
								let regexFlags = flags.includes('g') ? flags.replace('g', '') : flags;
								if (!regexFlags.includes('u')) {
									regexFlags += 'u';
								}
								const regex = new RegExp("(" + pattern + ")", regexFlags);
								
								let patternMatched = false;
								let testString = '';
								if (searchInDateField) {
									testString = str.d || '(no date)';
									patternMatched = str.d ? regex.test(str.d) : false;
								} else {
									testString = ((nestedCond.isCategory || fullPath) ? ("/" + str.p + "/" + str.n) : str.n);
									const dateStr = str.d || '';
									if (dateStr) testString += ' ' + dateStr;
									patternMatched = regex.test(testString);
								}
								
								// Debug logging for exact phrase patterns (boost >= 5)
								if (boost >= 5 && matchedCount < 5) {
									scoreLog.push(`[DEBUG] Testing exact phrase pattern: ${pattern.substring(0, 50)}, boost: ${boost}, flags: "${flags}", testString: ${testString.substring(0, 100)}, matched: ${patternMatched}`);
								}
								
								if (patternMatched) {
									subcondMatch++;
									if (boost > maxBoost) {
										maxBoost = boost;
										scoreLog.push(`[OR-Wrapper] Pattern matched: ${pattern.substring(0, 50)}, boost: ${boost}, maxBoost: ${maxBoost}, testString: ${testString.substring(0, 100)}`);
									}
									if (subcond.condition === "or") continue;
								} else if (boost >= 5 && matchedCount < 5) {
									scoreLog.push(`[DEBUG] Pattern NOT matched: ${pattern.substring(0, 50)}, boost: ${boost}, flags: "${flags}", testString: ${testString.substring(0, 100)}`);
								}
							} catch (e) { 
								if (boost >= 5 && matchedCount < 5) {
									scoreLog.push(`[DEBUG] Pattern regex error: ${pattern.substring(0, 50)}, error: ${e.message}`);
								}
							}
						}
						
						if ((subcond.condition === "or" && subcondMatch > 0) ||
							(subcond.condition === "and" && subcondMatch === (subcond.value || []).length)) {
							nestedCondMatch++;
							nestedScore += maxBoost;
						}
					}
					
					// Check if nested AND condition matched
					if ((nestedCond.condition === "and" && nestedCondMatch === (nestedCond.value || []).length) ||
						(nestedCond.condition === "or" && nestedCondMatch > 0)) {
						anyNestedMatched = true;
						if (nestedScore > bestNestedScore) bestNestedScore = nestedScore;
						// Don't break - check all to get best score
					}
				}
				
				if (anyNestedMatched) {
					matchCondition1++;
					fileScore += bestNestedScore;
					scoreLog.push(`[OR-Wrapper] Nested matched, added boost: ${bestNestedScore}, fileScore: ${fileScore}`);
				}
				level1Details.matched = anyNestedMatched;
				fileMatchDetails.push(level1Details);
				
				if (anyNestedMatched) matchConditionTotal++;
				continue; // Skip normal processing for this level1
			}
			
			for (const level2Idx in level1.value) {
				const level2 = level1.value[level2Idx];
				let matchCondition2 = 0;
				let maxBoostForThisCondition = 0;  // Track max boost for OR condition
				let sumBoostForThisCondition = 0;   // Track sum boost for AND condition
				let level2Details = {
					condition: level2.condition,
					matched: false,
					patterns: []
				};
				
				for (const v2Idx in level2.value) {
					const v2Item = level2.value[v2Idx];
					
					// CRITICAL: Handle nested conditions (e.g., AND inside OR for smart dates)
					if (typeof v2Item === 'object' && v2Item.condition) {
						let nestedMatchCount = 0;
						let nestedMaxBoost = 0;
						
						const subValues = v2Item.value || [];
						for (const subV of subValues) {
							const p = (typeof subV === 'object' && subV.pattern) ? subV.pattern : subV;
							const b = (typeof subV === 'object' && subV.boost) ? subV.boost : 1;
							const f = (typeof subV === 'object' && subV.flags !== undefined) ? subV.flags : (level2.modifier || "i");
							const rx = new RegExp("(" + p + ")", f.replace('g', '') + "u");
							
							const tStr = ((level1.isCategory || fullPath) ? ("/" + str.p + "/" + str.n) : str.n);
							if (rx.test(tStr)) {
								nestedMatchCount++;
								if (b > nestedMaxBoost) nestedMaxBoost = b;
							}
						}
						
						const isNestedMatched = (v2Item.condition === 'or' && nestedMatchCount > 0) || 
											   (v2Item.condition === 'and' && nestedMatchCount === subValues.length);
						
						if (isNestedMatched) {
							matchCondition2++;
							if (nestedMaxBoost > maxBoostForThisCondition) maxBoostForThisCondition = nestedMaxBoost;
						}
						continue;
					}

					// Support both old format (string) and new format ({ pattern, boost })
					const v2 = (typeof v2Item === 'object' && v2Item.pattern) ? v2Item.pattern : v2Item;
					const patternBoost = (typeof v2Item === 'object' && v2Item.boost) ? v2Item.boost : 1;
					const searchInDateField = (typeof v2Item === 'object' && v2Item.searchInDateField === true);
					
					// Explicitly check for undefined/null, not falsy (empty string '' is valid for case-sensitive)
					const flags = (typeof v2Item === 'object' && v2Item.flags !== undefined && v2Item.flags !== null) 
						? v2Item.flags 
						: (level2.modifier || "");
					// CRITICAL: Don't use global flag 'g' with test() - it causes state issues with repeated calls
					// CRITICAL: Add 'u' flag for Unicode support
					let regexFlags = flags.includes('g') ? flags.replace('g', '') : flags;
					if (!regexFlags.includes('u')) {
						regexFlags += 'u';
					}
					const substrRegex = new RegExp("(" + v2 + ")", regexFlags);
					let patternMatched = false;
					let effectiveBoost = patternBoost;
					
					if (colorize === false) {
						const dateStr = str.d || ''; // Date field 'd' in format YYYY-MM-DD
						if (searchInDateField) {
							// Date patterns: search ONLY in field 'd' (date), not in name/path
							patternMatched = dateStr ? substrRegex.test(dateStr) : false;
						} else {
							// Text patterns: search in name, path, and date (field 'd')
							// CRITICAL: Separate matches for prioritization
							const matchInName = substrRegex.test(str.n);
							const matchInPath = (level1.isCategory || fullPath) ? substrRegex.test("/" + str.p + "/") : false;
							const matchInDate = dateStr ? substrRegex.test(dateStr) : false;
							
							patternMatched = matchInName || matchInPath || matchInDate;
							
							if (patternMatched) {
								// Calculate weighted boost based on where it matched
								if (matchInName && matchInPath) {
									effectiveBoost = patternBoost * 1.2; // Both name and path: 120% priority
								} else if (matchInName) {
									effectiveBoost = patternBoost * 1.0; // Name: 100% priority
								} else if (matchInDate) {
									effectiveBoost = patternBoost * 0.8; // Date: 80% priority
								} else if (matchInPath) {
									effectiveBoost = patternBoost * 0.5; // Path only: 50% priority (DEPRIORITIZE)
								}
							}
						}
						
						if (patternMatched) {
							matchCondition2 += 1;
							// Accumulate boost: max for OR, sum for AND
							if (level2.condition === "or") {
								if (effectiveBoost > maxBoostForThisCondition) {
									maxBoostForThisCondition = effectiveBoost;
									scoreLog.push(`[OR] Pattern matched: ${v2.substring(0, 50)}, boost: ${patternBoost}, effective: ${effectiveBoost.toFixed(1)}, maxBoost: ${maxBoostForThisCondition.toFixed(1)}`);
								}
								// CRITICAL: DO NOT use continue here. Check all patterns to find highest weight.
							} else if (level2.condition === "and") {
								sumBoostForThisCondition += effectiveBoost;
								scoreLog.push(`[AND] Pattern matched: ${v2.substring(0, 50)}, boost: ${patternBoost}, effective: ${effectiveBoost.toFixed(1)}, sumBoost: ${sumBoostForThisCondition.toFixed(1)}`);
							}
						}
					} else if (colorize === true) {
						str = str.replace(substrRegex, '<b>$1</b>');
					}
					
					level2Details.patterns.push({
						pattern: v2,
						boost: patternBoost,
						regex: substrRegex.toString(),
						matched: patternMatched
					});
				}
				
				const level2Result = level2.condition == "or" && matchCondition2 > 0 || 
					(level2.condition == "and" && matchCondition2 == level2.value.length) || 
					(level2.condition == "not" && matchCondition2 == 0);
				level2Details.matched = level2Result;
				level2Details.maxBoost = maxBoostForThisCondition;
				
				// Add boost to file score if condition matched: max for OR, sum for AND
				if (level2Result) {
					if (level2.condition === "or" && maxBoostForThisCondition > 0) {
						fileScore += maxBoostForThisCondition;
						scoreLog.push(`[OR] Condition matched, added boost: ${maxBoostForThisCondition}, fileScore: ${fileScore}`);
					} else if (level2.condition === "and" && sumBoostForThisCondition > 0) {
						fileScore += sumBoostForThisCondition;
						scoreLog.push(`[AND] Condition matched, added boost: ${sumBoostForThisCondition}, fileScore: ${fileScore}`);
					}
				}
				
				if (level2Result) matchCondition1 += 1;
				level1Details.subConditions.push(level2Details);
			}
			
			const level1Result = level1.condition == "or" && matchCondition1 > 0 || 
				(level1.condition == "and" && matchCondition1 == level1.value.length) || 
				(level1.condition == "not" && matchCondition1 == 0);
			level1Details.matched = level1Result;
			
			if (level1Result) matchConditionTotal += 1;
			fileMatchDetails.push(level1Details);
		}
		
		const fileMatches = !colorize && matchConditionTotal == matchConditions.length;
		
		if (fileMatches) {
			// Always log first 10 matched files with score details (before incrementing matchedCount)
			if (matchedCount < 10 && scoreLog.length > 0) {
				console.log(`📊 [file-list.search] File matched: ${str.n}`, {
					fileScore,
					scoreLog,
					matchConditionTotal,
					matchConditionsLength: matchConditions.length
				});
			}
			// Also log match details for first 5 files
			if (processedCount <= 5) {
				console.log(`✅ [searchFiles] File ${processedCount} MATCHED:`, {
					name: str.n,
					path: str.p,
					score: fileScore,
					matchDetails: fileMatchDetails
				});
			}
			
			matches.push({ path: str.p, name: str.n, size: str.s, fullMode: fullPath, score: fileScore }); 
			matchedCount++; 
		} else if (colorize) { 
			matches.push(str); 
		}
		// Don't limit here - sort first, then limit
	}
	
	// Sort by score (higher = better match) if not colorizing
	if (!colorize && matches.length > 0) {
		matches.sort((a, b) => (b.score || 0) - (a.score || 0));
		console.log('📊 [searchFilesSinglePass] Sorted by score. Top 5:', matches.slice(0, 5).map(m => ({ name: m.name, score: m.score })));
		
		// Apply limit after sorting (only if limit is set and not Infinity)
		if (limit !== null && limit !== Infinity && matches.length > limit) {
			matches = matches.slice(0, limit);
		}
	}
	
	return colorize ? (matches[0]) : matches;
}

export function searchFiles(filesList, matchConditions, { fullPath = false, colorize = false, test = false, limit = SEARCH_LIMIT } = {}) {
	console.log('🔎 [searchFiles] ===== START =====');
	console.log('📥 [searchFiles] Input:', {
		filesCount: filesList.length,
		matchConditions: matchConditions,
		fullPath: fullPath,
		colorize: colorize,
		limit: limit
	});
	
	// For non-colorize mode, implement two-pass search: exact phrase first, then regular
	if (!colorize && matchConditions && matchConditions.length > 0) {
		const { exactPhraseConditions, regularConditions, wasOrWrapper } = splitConditionsByBoost(matchConditions);
		
		if (exactPhraseConditions.length > 0 || regularConditions.length > 0) {
			const exactPhraseResults = new Map(); // Map<fileKey, resultItem> to avoid duplicates
			const allResults = [];
			
			// Pass 1: Search ONLY exact phrase patterns (boost >= 5) through ENTIRE file array
			// CRITICAL: If we have BOTH exact phrase AND regular conditions, we need AND logic
			// Example: "эмоц \"усилие 14\"" → exact phrase "усилие 14" (boost 5) AND regular "эмоц" (boost 1)
			if (exactPhraseConditions.length > 0 && regularConditions.length > 0) {
				console.log('🔍 [searchFiles] Pass 1: Starting combined exact phrase + regular search (AND logic, full scan)', {
					exactPhraseConditionsCount: exactPhraseConditions.length,
					regularConditionsCount: regularConditions.length,
					totalFiles: filesList.length
				});
				
				// Combine both conditions into a single AND condition for Pass 1
				const combinedConditions = [];
				for (const exactCond of exactPhraseConditions) {
					if (exactCond.condition === "and" && exactCond.value && Array.isArray(exactCond.value)) {
						combinedConditions.push(...exactCond.value);
					} else {
						combinedConditions.push(exactCond);
					}
				}
				for (const regularCond of regularConditions) {
					if (regularCond.condition === "and" && regularCond.value && Array.isArray(regularCond.value)) {
						combinedConditions.push(...regularCond.value);
					} else {
						combinedConditions.push(regularCond);
					}
				}
				
				const combinedAndCondition = {
					condition: "and",
					value: combinedConditions,
					isCategory: false
				};
				
				// Search through ENTIRE array without limit - collect ALL exact phrase matches
				const exactMatches = searchFilesSinglePass(filesList, [combinedAndCondition], { fullPath, colorize, test, limit: null });
				for (const item of exactMatches) {
					const fileKey = `${item.path || ''}|${item.name || ''}`;
					exactPhraseResults.set(fileKey, item);
					allResults.push(item);
				}
				console.log('✅ [searchFiles] Pass 1 (exact phrase + AND regular) completed:', {
					exactMatchesCount: exactMatches.length,
					totalFilesScanned: filesList.length
				});
			} else if (exactPhraseConditions.length > 0) {
				console.log('🔍 [searchFiles] Pass 1: Starting exact phrase search (full scan)', {
					conditionsCount: exactPhraseConditions.length,
					totalFiles: filesList.length
				});
				// Search through ENTIRE array without limit - collect ALL exact phrase matches
				const exactMatches = searchFilesSinglePass(filesList, exactPhraseConditions, { fullPath, colorize, test, limit: null });
				for (const item of exactMatches) {
					const fileKey = `${item.path || ''}|${item.name || ''}`;
					exactPhraseResults.set(fileKey, item);
					allResults.push(item);
				}
				console.log('✅ [searchFiles] Pass 1 (exact phrase) completed:', {
					exactMatchesCount: exactMatches.length,
					totalFilesScanned: filesList.length
				});
			}
			
			// Pass 2: Search regular patterns (boost < 5) through ENTIRE file array
			// Only AFTER we collected all exact phrase results, we search for regular matches
			// Only if NO exact phrase conditions, OR if it was an OR wrapper (auto-exact phrase logic)
			if (regularConditions.length > 0 && (wasOrWrapper || exactPhraseConditions.length === 0)) {
				console.log('🔍 [searchFiles] Pass 2: Starting regular search (full scan)', {
					conditionsCount: regularConditions.length,
					totalFiles: filesList.length,
					alreadyFoundExact: exactPhraseResults.size
				});
				// Search through ENTIRE array without limit - collect ALL regular matches
				const regularMatches = searchFilesSinglePass(filesList, regularConditions, { fullPath, colorize, test, limit: null });
				for (const item of regularMatches) {
					const fileKey = `${item.path || ''}|${item.name || ''}`;
					// Only add if not already found in exact phrase search
					if (!exactPhraseResults.has(fileKey)) {
						allResults.push(item);
					}
				}
				const newMatchesCount = regularMatches.filter(item => {
					const fileKey = `${item.path || ''}|${item.name || ''}`;
					return !exactPhraseResults.has(fileKey);
				}).length;
				console.log('✅ [searchFiles] Pass 2 (regular) completed:', {
					regularMatchesCount: regularMatches.length,
					newMatchesCount: newMatchesCount,
					totalFilesScanned: filesList.length
				});
			}
			
			// Sort all results by score (descending) - exact phrase results (boost 5) will be first
			allResults.sort((a, b) => (b.score || 0) - (a.score || 0));
			
			// Mark results as coming from two-pass search to prevent fuzzy search from re-sorting
			// This preserves exact phrase priority (boost 5) over regular matches (boost 1)
			allResults.forEach(item => {
				item._twoPassSearch = true;
				item._hasExactPhrase = exactPhraseResults.has(`${item.path || ''}|${item.name || ''}`);
			});
			
			// Apply limit after sorting (for lazy loading - first 50, then next 50, etc.)
			const finalResults = allResults.length > limit ? allResults.slice(0, limit) : allResults;
			
			console.log('📊 [searchFiles] Two-pass search completed:', {
				totalResults: allResults.length,
				returned: finalResults.length,
				top5Scores: finalResults.slice(0, 5).map(item => ({ name: item.name, score: item.score }))
			});
			console.log('🔎 [searchFiles] ===== END =====');
			
			return finalResults;
		}
	}
	
	// Fallback to single-pass search (for colorize mode or if no conditions to split)
	// For single-pass, don't apply limit - let FilterManager handle it (will limit to 300)
	// LazyLoadManager will then paginate by 50
	console.log('🔍 [searchFiles] Using single-pass search');
	const result = searchFilesSinglePass(filesList, matchConditions, { fullPath, colorize, test, limit: null });
	console.log('📊 [searchFiles] Results:', {
		returned: Array.isArray(result) ? result.length : 1
	});
	console.log('🔎 [searchFiles] ===== END =====');
	return result;
}

export function createSearchMatcher(filesList, logger = window.HyperLog) {
	return function findMatches(query, callback) {
		if (window.cachedRequest) {
			window.cachedRequest['mCond'] = window.getMatchConditions(query);
			window.addCategoryMatchConditions(window.cachedRequest['mCond']);
		}
		const matchConditions = window.cachedRequest ? window.cachedRequest['mCond'] : [];
		let matches = searchFiles(filesList, matchConditions, { fullPath: false });
		let progress = 33;
		if (matches.length == 0) { matches = searchFiles(filesList, matchConditions, { fullPath: true }); progress = 67; }
		if (matches.length > 0) {
			if (window.searchMode != "fulltext") { if (typeof window.searchProgress === 'function') window.searchProgress(progress); }
			callback(matches);
		} else {
			if (typeof window.searchProgress === 'function') window.searchProgress(100);
		}
		if (typeof window.FileList?.updateResultsCount === 'function') {
			const hasMore = matches.length === SEARCH_LIMIT;
			window.FileList.updateResultsCount(matches.length, hasMore);
		}
		if (window.INITIALIZED2) { window.INITIALIZED2 = true; }
	};
}

export function highlightMatches(text, matchConditions) { return searchFiles(text, matchConditions, { colorize: true }); }


